// OverlayControlsPanel — Frontend-Steuerung für Overlays (öffentliche Karte). Ein OV-Button in der Karte öffnet ein
// Panel; die VIEW entscheidet, WO das Panel hängt (opts.container) und zu welcher Seite es gehört (opts.side) —
// z.B. Hauptkarte rechts, Galerie (rechtsbündig) links.
//
// Phase Frontend-UI: Schritt 2 = Button + leeres Panel (✓). Schritt 3 = Ebene 1 (Liste mit Auge-Toggle, Auswahl) (✓).
// Schritt 4 = Ebene 2 (Tool-Panel, öffnet bei Auswahl; Transparenz-Slider; erweiterbar via Tool-Registry).
// Es folgt: Gruppen-Zwischenliste.
//
// Verantwortung: NUR UI + Zustand. Anzeige (QuadOverlay/GroundOverlay) bleibt extern und wird über Callbacks gesteuert:
//   opts.onToggleVisible(id, on)  — Overlay auf der Karte ein-/ausblenden (overlays[].visible + arrangeOverlays)
//   opts.onSelect(id)             — Overlay gewählt (öffnet das Tool-Panel)
//   opts.onOpacity(id, fraction)  — Deckkraft 0..1 (overlays[].opacity + arrangeOverlays)
// google wird injiziert (für google.maps.ControlPosition).
import { resolveControlPosition } from "../adapter/providerConstants.js";
import { ICON_FIT_TO_VIEW } from "../tools/MapToolsMenu.js";   // Chef-Charge 4 (2): Zentrieren-Button-Icon
import { str } from "../../i18n/str.js";
import { linkEntity } from "../../topoEditor/entities/link.js";
import { docLinkEntity } from "../../topoEditor/entities/docLink.js";
import { overlayLinkEntity } from "../../topoEditor/entities/overlayLink.js";
import { parseTokensFromAny, renderFrontendHtmlFromTokens } from "../../topoEditor/topoDoc.js";
import { resolveDocs } from "../../topoEditor/docResolve.js";
// info_text-Frontend über die Nabe: [Text](href) → sichere <a target=_blank>, {id} → Doc-Link (topo2-link--intern),
// {ov:id} → interner Overlay-Link (topo2-ovlink) — Klick blendet das Overlay ein + zentriert (kein Gallery/Doc).
const INFO_ENTITIES = [linkEntity(), docLinkEntity(), overlayLinkEntity()];
// topo2 Button-Basis (Tokens + .topo2-btn/--solid) kommt global aus der topo2-Basis (stylesheet_vite/base.css),
// nicht mehr co-located importiert (entkoppelt 2026-06-26, topo2-Plan Phase 3).

export function createOverlayControlsClass(google) {

    // Auge: zentrale topo2-Quelle (.topo2-eye Mask in der globalen topo2-Basis: stylesheet_vite/src/topo2/icons.css) — kein Inline-SVG mehr, alle Augen gleich.
    // (EYE_OFF/durchgestrichen entfällt — Chef-Wunsch 11: inaktiv = graues normales Auge.)

    const PINK = '#c356a0';

    // Tool-Registry (Ebene 2): je Tool { key, render(item, api) → DOM }. `api` stellt die Panel-Callbacks bereit.
    // Erweiterbar — künftige Tools einfach hier (oder via opts.tools) ergänzen.
    const opacityTool = {
        key: 'opacity',
        render(item, api) {
            const wrap = document.createElement('div');
            const label = document.createElement('div');
            label.textContent = str('OVERLAY_OPACITY');   // Deckkraft
            label.style.cssText = 'font-size:12px;color:#666;margin-bottom:4px;padding-left:2px;';   // Chef-Charge 6: links bündig zu Auge/Slider
            const slider = document.createElement('input');
            slider.type = 'range';
            slider.min = '0';
            slider.max = '100';
            slider.value = String(Math.round((item.opacity != null ? item.opacity : 1) * 100));
            slider.className = 'topo2-range';   // Chef-Charge 6: eckiger Track (kein Browser-Rundung) + Akzent-Thumb
            slider.style.cssText = 'width:100%;';
            slider.disabled = !item.visible;   // Chef-Wunsch (10): Slider nur aktiv wenn das Overlay sichtbar ist
            slider.addEventListener('input', () => {
                const frac = parseInt(slider.value, 10) / 100;
                item.opacity = frac;              // lokalen Item-Zustand mitführen (für Re-Render)
                api.onOpacity(frac);
            });
            wrap.appendChild(label);
            wrap.appendChild(slider);
            return wrap;
        }
    };

    return class OverlayControlsPanel {
        constructor(map, opts = {}) {
            this._map = map;
            this._side = (opts.side === 'left') ? 'left' : 'right';   // Kontext-bestimmt (Main rechts, Galerie links)
            this._open = false;
            this._items = opts.items || [];
            this._onToggleVisible = (typeof opts.onToggleVisible === 'function') ? opts.onToggleVisible : null;
            this._onSelect = (typeof opts.onSelect === 'function') ? opts.onSelect : null;
            this._onOpacity = (typeof opts.onOpacity === 'function') ? opts.onOpacity : null;
            this._onReorder = (typeof opts.onReorder === 'function') ? opts.onReorder : null;   // Chef-Wunsch (9): Drag-Sort
            this._onOpenDoc = (typeof opts.onOpenDoc === 'function') ? opts.onOpenDoc : null;   // {id}-Doc-Link im Infotext → Gallery öffnen
            this._onActivateOverlay = (typeof opts.onActivateOverlay === 'function') ? opts.onActivateOverlay : null;   // {ov:id}-Overlay-Link → sichtbar + zentrieren
            this._onOpen = (typeof opts.onOpen === 'function') ? opts.onOpen : null;   // beim Öffnen: Liste nachladen (Consumer entscheidet, ob/woher)
            this._tools = opts.tools || [opacityTool];   // Tool-Registry (erweiterbar)
            this._selectedId = null;

            this._noButton = !!opts.noButton;   // Map-UI-Unify (Chef 16): Galerie-Auslöser kommt aus dem mapTools-Stack (Katalog)
            this._boundReposition = () => this._reposition();
            this._button = this._noButton ? null : this._buildButton();
            this._panel = this._buildPanel();
            this._renderList();
            this._syncButtonVisibility();   // panel-eigener Button: aus, wenn Liste leer (Main/Galerie nutzen das Katalog-Item)

            // Panel hängt AUSSERHALB der Karte (fixed im body) und wird beim Öffnen neben die Karte positioniert
            // (side = right/left). opts.container kann das überschreiben. Eigener Button nur OHNE noButton (sonst Katalog-Item).
            (opts.container || document.body).appendChild(this._panel);
            this._flyout = this._buildFlyout();   // Detail-Flyout rechts am Panel (½ Breite): Slider + Info-Pfeil
            this._flyoutInfoOpen = false;
            (opts.container || document.body).appendChild(this._flyout);
            if (this._button) {
                this._ctrlPos = resolveControlPosition(google, opts.buttonPosition || 'TOP_RIGHT');
                if (this._ctrlPos !== undefined) { map.controls[this._ctrlPos].push(this._button); }
            }
        }

        _buildButton() {
            const b = document.createElement('button');
            b.type = 'button';
            // topo2 Basis: Solid-Variante (Akzent-BG, weiße Schrift). Hover/Active kommen aus CSS (:hover/:active) statt JS-Handlern.
            // Disclosure-Button (öffnet ein Panel) → aria-expanded (a11y-korrekter als aria-pressed für reines Auf/Zu).
            b.className = 'topo-ov-button topo2-btn topo2-btn--solid topo2-btn--icon';
            b.title = str('OVERLAY_CONTROLS_BUTTON');            // Overlays
            b.setAttribute('aria-label', str('OVERLAY_CONTROLS_BUTTON'));
            b.setAttribute('aria-expanded', 'false');
            b.textContent = 'OV';
            // margin = Abstand zum Karten-Eck (Control-Positionierung, kein Button-Theming) → bewusst inline.
            b.style.margin = '10px';
            b.addEventListener('click', () => this.toggle());
            return b;
        }

        _buildPanel() {
            const p = document.createElement('div');
            p.className = 'topo-ov-panel topo-ov-panel--' + this._side;
            // Strukturelle Basis-Styles inline (Skelett); Feinschliff/Seite/Slide später per CSS-Klasse durch die View.
            // Flach (topo2): keine Rundung, kein Schatten. Bündig an der Map-Kante (Positionierung in _reposition).
            // Flex-Spalte: Header fix oben, Liste scrollt (flex:1), Tool-Panel (Slider) fix UNTEN. Sonst läge der Slider
            // im Scrollbereich der Liste und wäre erst nach Scrollen erreichbar (Bug overlays-opacity-slider-in-list).
            p.style.cssText = 'position:fixed;display:none;flex-direction:column;width:260px;overflow:hidden;background:#fff;'
                + 'border:1px solid #bbb;border-radius:0;box-shadow:none;font-size:12px;z-index:1200;';   // 12px; nur Größe (Family erbt die Frontend-Verdana). Zeilenhöhe unverändert — die wird von den Icons (Auge/Zentrieren 18px) bestimmt, nicht vom Text

            // Chef-Wunsch (16): Header-Zeile (Topothek-Rosa) mit Titel + Schließkreuz; sticky → bleibt beim Scrollen oben.
            const header = document.createElement('div');
            header.className = 'topo-ov-header';
            header.style.cssText = 'position:sticky;top:0;z-index:1;display:flex;align-items:center;justify-content:space-between;'
                + 'padding:6px 10px;background:var(--topo2-accent);color:var(--topo2-text-on-accent);';   // Titel nicht fett (font-weight:bold entfernt)
            const headerTitle = document.createElement('span');
            headerTitle.textContent = str('OVERLAY_CONTROLS_BUTTON');   // Overlays
            const closeBtn = document.createElement('button');
            closeBtn.type = 'button';
            closeBtn.setAttribute('aria-label', str('OVERLAY_PANEL_CLOSE'));
            closeBtn.innerHTML = '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><g fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></g></svg>';
            closeBtn.style.cssText = 'border:none;background:none;box-shadow:none;color:inherit;cursor:pointer;padding:2px;display:flex;';
            closeBtn.addEventListener('click', () => this.close());
            header.appendChild(headerTitle);
            header.appendChild(closeBtn);
            p.appendChild(header);

            const list = document.createElement('div');
            list.className = 'topo-ov-list';
            list.style.cssText = 'flex:1 1 auto;overflow:auto;';   // nur die Liste scrollt; Header + Tools bleiben fix
            p.appendChild(list);
            this._list = list;

            // Ebene 2 (Tool-Panel): versteckt bis ein Overlay gewählt ist.
            const tools = document.createElement('div');
            tools.className = 'topo-ov-tools';
            tools.style.cssText = 'display:none;flex:0 0 auto;border-top:2px solid #eee;padding:8px 10px;';
            p.appendChild(tools);
            this._toolPanel = tools;

            return p;
        }

        // --- Liste (Ebene 1) ---

        setItems(items) { this._items = items || []; this._renderList(); this._syncButtonVisibility(); }

        // Panel-EIGENER Button (nur bei noButton=false, z.B. Standalone/Locate): ausblenden, wenn die Liste leer ist.
        // ACHTUNG: Main/Galerie nutzen NICHT diesen Button, sondern das mapUiConfig-Katalog-Item 'overlays' (Reconciler) —
        // dessen Sichtbarkeit hängt am Katalog/Controller, NICHT hier.
        _syncButtonVisibility() {
            if (this._button) { this._button.style.display = this._items.length ? '' : 'none'; }
        }

        // Chef-Wunsch (9): Drag-Sort — `fromId` an die Position von `toId` einsortieren, neu rendern, neue Reihenfolge melden.
        _reorder(fromId, toId) {
            if (fromId == null || String(fromId) === String(toId)) { return; }
            const from = this._items.findIndex((it) => String(it.id) === String(fromId));
            if (from < 0) { return; }
            const [moved] = this._items.splice(from, 1);
            let to = this._items.findIndex((it) => String(it.id) === String(toId));
            if (to < 0) { to = this._items.length; }
            this._items.splice(to, 0, moved);
            this._renderList();
            if (this._onReorder) { this._onReorder(this._items.map((it) => it.id)); }
        }

        _renderList() {
            if (!this._list) { return; }
            this._selectedId = null;
            if (this._toolPanel) { this._toolPanel.style.display = 'none'; this._toolPanel.innerHTML = ''; }
            if (this._flyout) { this._flyout.style.display = 'none'; }   // Auswahl weg → Flyout zu
            this._flyoutInfoOpen = false;
            this._list.innerHTML = '';
            if (!this._items.length) {
                this._list.innerHTML = '<div style="padding:10px;color:#666;">' + str('OVERLAY_LIST_EMPTY') + '</div>';   // keine Overlays
                return;
            }
            // Gruppen-Überschriften (z.B. „Extern" / „Archiv") nur, wenn wirklich mehr als eine Gruppe da ist —
            // sonst stünde über der gewohnten Liste eine einzelne sinnlose Zeile. Die Items kommen bereits
            // gruppiert sortiert; group/groupLabel liefert der Controller.
            const groups = new Set(this._items.map((it) => it.group || ''));
            const withHeaders = groups.size > 1;
            let lastGroup = null;
            for (let i = 0; i < this._items.length; i++) {
                const it = this._items[i];
                if (withHeaders && it.group !== lastGroup) {
                    this._list.appendChild(this._makeGroupHeader(it.groupLabel || it.group));
                    lastGroup = it.group;
                }
                this._list.appendChild(this._makeItem(it));
            }
        }

        // Überschrift einer Listengruppe. Sticky, damit sie beim Scrollen der Liste stehen bleibt (Optik in
        // topo2/overlay-panel.css — hier bewusst kein weiteres Inline-Style-Konvolut).
        _makeGroupHeader(label) {
            const h = document.createElement('div');
            h.className = 'topo-ov-group';
            h.textContent = label || '';
            return h;
        }

        _makeItem(item) {
            const row = document.createElement('div');
            row.className = 'topo-ov-item';
            row.dataset.id = item.id;
            row.style.cssText = 'display:flex;align-items:center;gap:8px;padding:6px 10px;cursor:pointer;border-bottom:1px solid #eee;';

            const eye = document.createElement('button');
            eye.type = 'button';
            eye.className = 'topo-ov-eye';
            // Chef-Wunsch (11): flaches Symbol — keine Border, kein Schatten (sonst erbt es den Legacy-`body button`-Schatten).
            eye.style.cssText = 'flex:0 0 auto;border:none;background:none;box-shadow:none;padding:2px;cursor:pointer;display:flex;';
            this._applyEye(eye, !!item.visible);
            eye.addEventListener('click', (e) => { e.stopPropagation(); this._toggleItem(item.id, eye); this._selectItem(item.id, row); });
            eye.addEventListener('mousedown', (e) => e.preventDefault());   // kein Fokus-Hintergrund beim Klick (Legacy body button:focus)

            const label = document.createElement('span');
            label.className = 'topo-ov-name';
            label.style.cssText = 'flex:1 1 auto;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#222;';
            label.textContent = item.name || '';   // nur der Titel — das Jahr steht jetzt im Info-Feld unten (Titel:/Datum:)
            label.title = label.textContent;   // voller Name als Tooltip — die Liste kürzt lange Namen mit Ellipsis (…)

            // Chef-Wunsch (9): Sort-Handle zwischen Auge und Titel — unsichtbar, bei Zeilen-Hover sichtbar. Drag NUR über den Handle.
            const handle = document.createElement('span');
            handle.className = 'topo-ov-sorthandle';
            handle.textContent = '⠿';
            handle.style.cssText = 'display:none;';   // Frontend: Sort-Handle ausgeblendet + Drag deaktiviert (nicht greifbar) → Titel (flex:1) bekommt die Breite bis zum Auge; Sortierung nur im Admin
            handle.addEventListener('mousedown', () => { row.draggable = true; });
            handle.addEventListener('mouseup', () => { row.draggable = false; });   // Klick ohne Drag → wieder fest
            handle.addEventListener('click', (e) => { e.stopPropagation(); });      // Griff-Klick togglet NICHT die Sichtbarkeit
            // Hover/Selected-Tint macht jetzt die CSS (topo2/overlay-panel.css: :hover:not(.is-selected) bzw. .is-selected).
            // Hier nur noch der Sort-Handle-Fade — die Handle-Sichtbarkeit hängt am Zeilen-Hover.
            row.addEventListener('mouseenter', () => { handle.style.opacity = '1'; });
            row.addEventListener('mouseleave', () => { handle.style.opacity = '0'; });

            row.addEventListener('dragstart', (e) => { this._dragId = item.id; if (e.dataTransfer) { e.dataTransfer.effectAllowed = 'move'; } row.style.opacity = '0.5'; });
            row.addEventListener('dragover', (e) => { e.preventDefault(); });
            row.addEventListener('drop', (e) => { e.preventDefault(); this._reorder(this._dragId, item.id); });
            row.addEventListener('dragend', () => { row.draggable = false; row.style.opacity = ''; this._dragId = null; });

            // Chef-Charge 4 (2): rechts ein leerer Spacer (= Sort-Handle-Breite, symmetrisch) + Zentrieren-Button (Karten-Icon).
            const spacer = document.createElement('span');
            spacer.style.cssText = 'flex:0 0 14px;';
            const center = document.createElement('button');
            center.type = 'button';
            center.className = 'topo-ov-center';
            center.style.cssText = 'flex:0 0 auto;border:none;background:none;box-shadow:none;padding:2px;cursor:pointer;display:flex;color:var(--topo2-accent);';
            center.innerHTML = ICON_FIT_TO_VIEW;
            center.title = str('MAP_TOOL_CENTER');   // zentrieren
            const csvg = center.querySelector('svg');
            if (csvg) { csvg.style.width = '18px'; csvg.style.height = '18px'; }
            // „Disabled"-Look via Klasse (CSS), NICHT via `disabled`-Attribut → umgeht die Legacy-`button[disabled]`-Grauregel.
            // Die Aktion blockt der item.visible-Guard im Click-Handler unten; der Klick geht wegen stopPropagation eh nicht an die Zeile.
            center.classList.toggle('is-disabled', !item.visible);
            // Chef-Charge 6: Zentrieren = NUR hinspringen (Pan/Fit), und nur wenn das Overlay sichtbar ist (Guard).
            // Das Auswählen/Slider-Zeigen macht jetzt der Zeilen-Klick.
            center.addEventListener('click', (e) => {
                e.stopPropagation();
                // Erst flach stellen: ein geneigtes Fit/Pan springt sonst auf einen leeren Fleck (Zentrieren bei Tilt).
                if (this._map && this._map.setTilt) { this._map.setTilt(0); }
                if (item.visible && this._onSelect) { this._onSelect(item.id); }
            });
            center.addEventListener('mousedown', (e) => e.preventDefault());   // kein Fokus-Hintergrund beim Klick (Legacy body button:focus)

            row.appendChild(eye);
            row.appendChild(handle);
            row.appendChild(label);
            row.appendChild(spacer);
            row.appendChild(center);
            // Zeilen-Klick = Sichtbarkeit togglen UND auswählen (wie das Auge). Auge/Zentrieren haben stopPropagation.
            row.addEventListener('click', () => { this._toggleItem(item.id, eye); this._selectItem(item.id, row); });
            return row;
        }

        _applyEye(eye, visible) {
            // Chef-Wunsch (11): inaktiv = graues NORMALES Auge (nicht durchgestrichen), flach. Auge = zentrales .topo2-eye-Token.
            eye.innerHTML = '<i class="topo2-eye"></i>';
            eye.style.color = visible ? PINK : '#aaa';
            eye.dataset.visible = visible ? '1' : '0';
            eye.title = visible ? str('OVERLAY_HIDE') : str('OVERLAY_SHOW');   // ausblenden / einblenden
        }

        _toggleItem(id, eye) {
            const on = eye.dataset.visible !== '1';
            this._applyEye(eye, on);
            const item = this._findItem(id);
            if (item) { item.visible = on; }
            // Zentrieren-Button der Zeile mit dem visible-Zustand synchronisieren (die Zeile wird beim Toggle nicht neu gerendert).
            const rowEl = eye.closest('.topo-ov-item');
            const centerBtn = rowEl && rowEl.querySelector('.topo-ov-center');
            if (centerBtn) { centerBtn.classList.toggle('is-disabled', !on); }
            if (this._onToggleVisible) { this._onToggleVisible(id, on); }
            // Chef-Wunsch (10): ist das umgeschaltete Item gerade gewählt → Tool-Panel neu rendern (Slider folgt visible).
            if (String(id) === String(this._selectedId)) { this._renderFlyout(item); }
        }

        // --- Auswahl + Tool-Panel (Ebene 2) ---

        _selectItem(id, row) {
            this._selectedId = id;
            const item = this._findItem(id);
            const rows = this._list.querySelectorAll('.topo-ov-item');
            for (let i = 0; i < rows.length; i++) { rows[i].classList.toggle('is-selected', rows[i] === row); }
            this._renderFlyout(item);
            // Chef-Charge 4 (2): KEIN Auto-Sprung mehr beim Wählen — der Sprung (Pan/Fit) läuft jetzt über den Zentrieren-Button.
        }

        _renderTools(item) {
            if (!this._toolPanel) { return; }
            this._toolPanel.innerHTML = '';
            if (!item) { this._toolPanel.style.display = 'none'; return; }
            const api = { onOpacity: (frac) => { if (this._onOpacity) { this._onOpacity(item.id, frac); } } };
            for (let i = 0; i < this._tools.length; i++) {
                const el = this._tools[i].render(item, api);
                if (el) { this._toolPanel.appendChild(el); }
            }
            // Info-Bereich unter dem Slider: voller Titel + Jahr des gewählten Overlays (Chef-Präsentation).
            // Vorbereitet als eigener Block — hier später weitere Infos ergänzbar.
            this._toolPanel.appendChild(this._buildInfo(item));
            this._toolPanel.style.display = 'block';
        }

        // --- Detail-Flyout (rechts am Panel, ½ Breite): Slider + Info-Pfeil (►); ► klappt Titel/Datum darunter auf ---
        _buildFlyout() {
            const f = document.createElement('div');
            f.className = 'topo-ov-flyout';
            f.style.cssText = 'position:fixed;display:none;width:max-content;background:#fff;'
                + 'border:1px solid #bbb;box-shadow:none;font-size:12px;z-index:1200;';   // Breite = breitestes Kind (Kopf 130 / Info 260)
            return f;
        }

        // Inhalt des Flyouts fürs gewählte Item rendern + zeigen. Slider nur aktiv wenn sichtbar; Info-Pfeil (►) immer.
        _renderFlyout(item) {
            if (!this._flyout) { return; }
            this._flyout.innerHTML = '';
            if (!item) { this._flyout.style.display = 'none'; return; }

            const head = document.createElement('div');
            head.style.cssText = 'display:flex;align-items:center;gap:6px;width:130px;height:32px;box-sizing:border-box;padding:6px 16px;';   // Kopf fix 130 + Zeilenhöhe; wächst NICHT mit dem Info-Container. Padding 6 (war 8) → der 19px-Info-Button passt in den Innenraum
            const slider = document.createElement('input');
            slider.type = 'range'; slider.min = '0'; slider.max = '100';
            slider.value = String(Math.round((item.opacity != null ? item.opacity : 1) * 100));
            slider.className = 'topo2-range';
            slider.style.cssText = 'flex:1 1 auto;min-width:0;height:20px;margin:0;';   // in die 30px-Kopfhöhe passen (nicht höher als die Zeile)
            slider.disabled = !item.visible;   // Slider nur aktiv wenn das Overlay sichtbar ist
            slider.addEventListener('input', () => {
                const frac = parseInt(slider.value, 10) / 100;
                item.opacity = frac;
                if (this._onOpacity) { this._onOpacity(item.id, frac); }
            });
            // Info-Toggle: quadratischer topo2-Button mit „i" (war ein randloser Chevron, der beim Aufklappen kippte).
            // Zustand über die Variante: zu = outline (weiß, Akzentrahmen), offen = solid — die Rotation als Signal
            // entfällt mit dem Chevron. Größe: extrasmall (19px) = die kleinste topo2-Stufe, passt in die Kopfzeile.
            const infoBtn = document.createElement('button');
            infoBtn.type = 'button';
            infoBtn.className = 'topo-ov-infotoggle topo2-btn topo2-btn--icon topo2-btn--extrasmall';
            infoBtn.style.cssText = 'flex:0 0 auto;';   // nur Flex-Verhalten; Optik/Maße kommen aus den topo2-Klassen
            infoBtn.textContent = 'i';
            infoBtn.title = str('OVERLAY_INFO');
            infoBtn.setAttribute('aria-label', str('OVERLAY_INFO'));
            infoBtn.addEventListener('mousedown', (e) => e.preventDefault());
            head.appendChild(slider);
            head.appendChild(infoBtn);
            this._flyout.appendChild(head);

            // Info-Bereich (Titel/Datum) darunter — nur wenn ► aktiv; Breite = Flyout (= Slider+Button).
            const info = this._buildInfo(item);
            info.style.cssText = 'width:260px;box-sizing:border-box;padding:6px 16px;border-top:1px solid #eee;';   // Info-Container 200% breit (= 2× Kopf), wächst nach unten
            info.style.display = this._flyoutInfoOpen ? '' : 'none';
            this._applyInfoToggleState(infoBtn);
            infoBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                this._flyoutInfoOpen = !this._flyoutInfoOpen;
                info.style.display = this._flyoutInfoOpen ? '' : 'none';
                this._applyInfoToggleState(infoBtn);
                this._positionFlyout();
            });
            this._flyout.appendChild(info);

            this._flyout.style.display = 'block';
            this._positionFlyout();
        }

        // Auf/Zu am Info-Toggle spiegeln: zu = outline, offen = solid. aria-expanded für Screenreader (Disclosure-Button).
        _applyInfoToggleState(infoBtn) {
            const open = this._flyoutInfoOpen;
            infoBtn.classList.toggle('topo2-btn--solid', open);
            infoBtn.classList.toggle('topo2-btn--outline', !open);
            infoBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
        }

        // Flyout rechts ans Panel andocken (Galerie/left → nach links), vertikal auf Höhe der gewählten Zeile (in Panel-Grenzen).
        _positionFlyout() {
            if (!this._flyout || !this._open || this._flyout.style.display === 'none') { return; }
            const pr = this._panel.getBoundingClientRect();
            const fw = this._flyout.offsetWidth || 130;
            const left = (this._side === 'left') ? (pr.left - fw) : pr.right;
            this._flyout.style.left = Math.max(0, left) + 'px';
            let top = pr.top;
            if (this._list) {
                const rowsE = this._list.querySelectorAll('.topo-ov-item');
                for (let i = 0; i < rowsE.length; i++) {
                    if (String(rowsE[i].dataset.id) === String(this._selectedId)) { top = rowsE[i].getBoundingClientRect().top; break; }
                }
            }
            top = Math.max(top, pr.top);   // oben bündig mit der gewählten Zeile, aber nie über die Panel-Oberkante
            // Bottom-Clamp: bei langer Liste liegt die gewählte Zeile weit unten → das nach unten wachsende Flyout
            // (Info-Block aufgeklappt) liefe sonst aus dem Fenster. Höhe hier messen, NACHDEM der Inhalt steht.
            // Ist das Flyout höher als das Fenster, gewinnt die Oberkante (Math.max unten) → Kopf mit Slider bleibt sichtbar.
            const fh = this._flyout.offsetHeight || 32;
            top = Math.min(top, window.innerHeight - fh);
            this._flyout.style.top = Math.max(0, top) + 'px';
        }

        // Info-Block (unter dem Slider): je Zeile dezentes Label + voller Wert ([Titel:] <Name>, [Jahr:] <Jahr>). Titel in
        // Panel-Schriftgröße (12px), weitere Infos 1px kleiner (11px). Werte NICHT gekürzt (anders als die Liste) → umbrechend.
        _buildInfo(item) {
            const info = document.createElement('div');
            info.className = 'topo-ov-info';
            info.style.cssText = 'margin-top:8px;padding-top:8px;border-top:1px solid #eee;';
            info.appendChild(this._infoLine(str('NAME') + ':', item.name || '', 12));   // Label „Name" (Chef-Wunsch: Titel→Name)
            const year = (item.year != null && String(item.year) !== '0') ? String(item.year) : '';
            // Merge (2026-07-13): YEAR-Label aus master (Chef-Wunsch Datum→Jahr), Info_text-Rendering aus dem Feature (Editor/Nabe).
            if (year) { info.appendChild(this._infoLine(str('YEAR') + ':', year, 12)); }   // Label „Jahr" (Chef-Wunsch: Datum→Jahr)
            // Freitext (Lauftext) über die Nabe: [Text](href) → echte <a target=_blank> (nur http/https/mailto),
            // {id} → interner Doc-Link (topo2-link--intern, data-doc-id). Rest escaped; sicheres HTML (kein Roh-innerHTML).
            if (item.info_text) {
                const html = renderFrontendHtmlFromTokens(parseTokensFromAny(item.info_text, INFO_ENTITIES), INFO_ENTITIES);
                const line = this._infoLine(str('OVERLAY_INFO') + ':', item.info_text, 11, { html });
                info.appendChild(line);
                this._decorateDocLinks(line);   // async: grau/„nicht veröffentlicht" + Fremd-Archiv-Decorator
            }
            // Interne Links im Infotext (delegiert): {ov:id} → Overlay sichtbar + zentrieren; {id} → Gallery öffnen
            // (unpublished/gone = kein Öffnen).
            info.addEventListener('click', (e) => {
                const ov = e.target.closest('.topo2-ovlink[data-ov-id]');
                if (ov) {
                    e.preventDefault();
                    const ovId = ov.getAttribute('data-ov-id');
                    if (ovId && this._onActivateOverlay) { this._onActivateOverlay(ovId); }
                    return;
                }
                const a = e.target.closest('.topo2-doclink[data-doc-id]');
                if (!a || a.classList.contains('is-unpublished')) { return; }
                e.preventDefault();
                const did = a.getAttribute('data-doc-id');
                if (did && this._onOpenDoc) { this._onOpenDoc(did); }
            });
            return info;
        }

        // Doc-Links ({id}) in einer Infozeile per Batch auflösen und markieren:
        //   • nicht existent ODER unpublished → grau, kein Öffnen, Tooltip „nicht veröffentlicht", KEINE Vorschau
        //   • freigegeben (eigenes ODER fremdes Archiv) → Hover-Vorschaubild, KEIN Tooltip
        //   • fremdes Archiv zusätzlich → Fremd-Decorator (::after, Symbol für „aus dem Archiv raus")
        //
        // Die Vorschau hängt am delegierten Handler in basic.js (`.hoverThumb` + `rel`) — derselbe Mechanismus,
        // den der Legacy-Weg `{docid}` in der Kommentarspalte schon nutzt, statt eines zweiten daneben.
        // Die Klasse wird NUR für freigegebene Dokumente gesetzt: `action=documentThumb` filtert selbst weder
        // auf Freigabe noch auf Archiv, blind gesetzt zeigte die Vorschau also auch Bilder, die der Besucher
        // nicht sehen soll. Hier ist die Auskunft schon da — `resolveDocs` liefert exists/published/own.
        // Async + best-effort: bei Netzfehler bleibt der Link im Default-Zustand.
        _decorateDocLinks(lineEl) {
            const anchors = Array.prototype.slice.call(lineEl.querySelectorAll('.topo2-doclink[data-doc-id]'));
            if (!anchors.length) { return; }
            resolveDocs(anchors.map((a) => a.getAttribute('data-doc-id'))).then((map) => {
                anchors.forEach((a) => {
                    const id = a.getAttribute('data-doc-id');
                    const r = map[id];
                    if (!r) { return; }
                    if (!r.exists || !r.published) {
                        a.classList.add('is-unpublished');
                        a.title = str('DOC_LINK_UNPUBLISHED');
                        return;
                    }
                    // Freigegeben: Vorschau statt Tooltip. Ein Doc-Link ist kein externer Link — die Ziel-URL
                    // im Titel zu zeigen hilft hier niemandem, das Bild schon (User 2026-09-23).
                    a.classList.add('hoverThumb');
                    a.setAttribute('rel', id);
                    if (r.own === false) { a.classList.add('is-foreign'); }   // Symbol „aus dem Archiv raus"
                });
            });
        }

        // Eine Info-Zeile: dezentes Label in FESTER Spaltenbreite → alle Werte starten linksbündig untereinander;
        // Wert füllt den Rest (umbrechend). fontPx: alle Info-Zeilen 12px (= Panel).
        _infoLine(labelText, valueText, fontPx, opts) {
            // Beschriftung ÜBER dem Wert, beide an derselben Kante (User 2026-09-25) — über die topo2-Klasse
            // `topo2-field-stack`, dieselbe, die auch das Editor-Popover nutzt. Vorher stand die Beschriftung in
            // einer festen 46px-Spalte daneben: bei längeren Beschriftungen wurde es eng, und der Wert begann
            // immer eingerückt. Die übrige Darstellung wandert aus den Inline-Styles ins Stylesheet; hier bleibt
            // nur die Schriftgröße, weil sie je Zeile verschieden ist (Name/Jahr 12px, Infotext 11px).
            const row = document.createElement('div');
            row.className = 'topo-ov-info-line topo2-field-stack';
            row.style.fontSize = fontPx + 'px';
            const lab = document.createElement('span');
            lab.className = 'topo-ov-info-label topo2-field-label';
            lab.textContent = labelText;
            const val = document.createElement('span');
            val.className = 'topo-ov-info-value';
            // opts.html = vorgerendertes sicheres HTML (z.B. Links); sonst Plain-Text via textContent (auto-escaped).
            if (opts && opts.html != null) { val.innerHTML = opts.html; } else { val.textContent = valueText; }
            row.appendChild(lab);
            row.appendChild(val);
            return row;
        }

        _findItem(id) {
            for (let i = 0; i < this._items.length; i++) {
                if (String(this._items[i].id) === String(id)) { return this._items[i]; }
            }
            return null;
        }

        // --- Auf/Zu ---

        open() {
            this._open = true;
            // Der Consumer darf hier die Liste auffrischen. Bewusst BEIM ÖFFNEN und nicht laufend: in diesem Moment
            // ist nichts gewählt (close() und _renderList() setzen die Auswahl zurück), ein setItems aus der Antwort
            // nimmt dem Nutzer also nichts weg. Das Panel geht sofort auf, die Antwort kommt nach.
            if (this._onOpen) { this._onOpen(); }
            this._panel.style.display = 'flex';   // Flex-Spalte (s. _buildPanel): Liste scrollt, Slider bleibt unten fix
            this._reposition();
            if (this._button) { this._button.classList.add('is-open'); this._button.setAttribute('aria-expanded', 'true'); }
            window.addEventListener('scroll', this._boundReposition, true);
            window.addEventListener('resize', this._boundReposition);
            this._startFollow();   // dem (evtl. verschiebbaren) Karten-Container folgen, solange offen
        }
        close() {
            this._open = false;
            this._panel.style.display = 'none';
            if (this._flyout) { this._flyout.style.display = 'none'; }
            // Beim Schließen deselektieren: Auswahl + Zeilen-Markierung + Flyout-Info zurücksetzen (sonst bleibt beim Wiederöffnen alt gewählt).
            this._selectedId = null;
            this._flyoutInfoOpen = false;
            if (this._list) { this._list.querySelectorAll('.topo-ov-item').forEach((r) => { r.classList.remove('is-selected'); }); }
            if (this._button) { this._button.classList.remove('is-open'); this._button.setAttribute('aria-expanded', 'false'); }
            window.removeEventListener('scroll', this._boundReposition, true);
            window.removeEventListener('resize', this._boundReposition);
            this._stopFollow();
        }
        // Folgt dem Karten-Container, solange offen — repositioniert, wenn sich dessen Bildschirmposition ändert (z.B. wenn
        // ein verschiebbares Popup mit der Karte gezogen wird). Scroll/Resize deckt der Listener oben ab; rAF nur solange offen.
        _startFollow() {
            this._stopFollow();
            // ResizeObserver auf dem Karten-Container → folgt zuverlässig GRÖSSEN-Änderungen (Karten-Resize-Handle), die die
            // rAF-Positionsprüfung u.U. nicht rechtzeitig als Rect-Änderung sieht. rAF darunter deckt reine Positions-Verschiebung ab.
            if (typeof ResizeObserver !== 'undefined' && this._map && this._map.getDiv) {
                this._followRO = new ResizeObserver(() => { if (this._open) { this._reposition(); } });
                this._followRO.observe(this._map.getDiv());
            }
            let lastKey = '';
            const tick = () => {
                if (!this._open || !this._map || !this._map.getDiv) { this._followRaf = null; return; }
                const r = this._map.getDiv().getBoundingClientRect();
                const key = r.left + ',' + r.top + ',' + r.width + ',' + r.height;
                if (key !== lastKey) { lastKey = key; this._reposition(); }
                this._followRaf = requestAnimationFrame(tick);
            };
            this._followRaf = requestAnimationFrame(tick);
        }
        _stopFollow() {
            if (this._followRaf) { cancelAnimationFrame(this._followRaf); this._followRaf = null; }
            if (this._followRO) { this._followRO.disconnect(); this._followRO = null; }
        }

        toggle() { if (this._open) { this.close(); } else { this.open(); } }
        isOpen() { return this._open; }

        // Lebt das Panel noch? Mit eigenem Button: hängt der noch in den Karten-Controls. Ohne Button (Katalog-Auslöser):
        // ist das Panel-Element noch im DOM (Karten-Refresh leert die Controls, das body-Panel überlebt).
        isAlive() {
            if (this._button) { return !!(this._map && this._map.getDiv && this._map.getDiv().contains(this._button)); }
            return !!(this._panel && this._panel.parentNode);
        }

        // Positioniert das Panel beim Öffnen NEBEN der Karte (außerhalb) — über deren echte Bildschirm-Position
        // (getBoundingClientRect) → robust in jedem Layout; folgt Scroll/Resize.
        //   - right (Main): rechts der Karte, OBEN ausgerichtet; Höhe auf die Kartenhöhe gedeckelt.
        //   - left (Galerie): links der Karte, OBEN ausgerichtet (top am oberen Karten-Eck). Höhe NICHT auf die flache
        //     Galerie-Karte gedeckelt, sondern auf den Platz bis zum Fensterboden — die Liste darf die Fensterhöhe
        //     nutzen. (Löst die frühere Unten-Ausrichtung „Chef 16 B" ab; das Panel klappt jetzt nach unten auf, weg
        //     vom auslösenden mapTools-Button links unten.)
        _reposition() {
            if (!this._open || !this._map.getDiv) { return; }
            const r = this._map.getDiv().getBoundingClientRect();
            const w = this._panel.offsetWidth || 260;
            if (this._side === 'left') {
                const top = Math.max(0, r.top);   // Karte oben aus dem Fenster gescrollt → am Fensterrand kleben
                this._panel.style.maxHeight = Math.max(80, window.innerHeight - top) + 'px';
                this._panel.style.left = Math.max(0, r.left - w) + 'px';
                this._panel.style.top = top + 'px';
            } else {
                // Frontend: bündig an der RECHTEN Map-Kante, OBEN ausgerichtet (oben↔oben), volle Map-Höhe.
                this._panel.style.maxHeight = Math.max(80, r.height) + 'px';
                this._panel.style.left = r.right + 'px';
                this._panel.style.top = r.top + 'px';
            }
            this._positionFlyout();   // Flyout dem Panel nachziehen
        }

        getPanel() { return this._panel; }
        getButton() { return this._button; }
        getSelectedId() { return this._selectedId; }

        // Seite umstellen (falls Galerie/Main dieselbe Pool-Karte teilen → Kontextwechsel ändert die Abklapp-Richtung).
        setSide(side) {
            this._side = (side === 'left') ? 'left' : 'right';
            if (this._open) { this._reposition(); }
        }

        // Callbacks auffrischen (z.B. nach Gallery<->Main-Wechsel: neue Closure mit frischem overlays/arrangeOverlays).
        setCallbacks(onToggleVisible, onSelect, onOpacity, onReorder) {
            if (typeof onToggleVisible === 'function') { this._onToggleVisible = onToggleVisible; }
            if (typeof onSelect === 'function') { this._onSelect = onSelect; }
            if (typeof onOpacity === 'function') { this._onOpacity = onOpacity; }
            if (typeof onReorder === 'function') { this._onReorder = onReorder; }
        }

        // Eigener Setter statt eines fünften Parameters oben: setCallbacks ist positional, und ein weiterer Platz
        // würde jeden Aufrufer zwingen, ihn mitzuzählen. Wird beim Wiederverwenden eines lebenden Panels gebraucht —
        // dort muss die neue Closure (frisches buildItems) nachgereicht werden, sonst füllt das Nachladen die Liste
        // aus den Daten des vorigen Kartenkontexts.
        setOnOpen(fn) { this._onOpen = (typeof fn === 'function') ? fn : null; }

        // Sichtbarkeit von außen setzen (z.B. {ov:id}-Link-Aktivierung): Item + Auge + Zentrieren-Button nachziehen,
        // OHNE onToggleVisible zu feuern (der Store wurde vom Aufrufer bereits gepatcht). Gezieltes Setzen, kein Toggle.
        setItemVisible(id, on) {
            const item = this._findItem(id);
            if (item) { item.visible = !!on; }
            const rowEl = this._list && this._list.querySelector('.topo-ov-item[data-id="' + id + '"]');
            if (rowEl) {
                const eye = rowEl.querySelector('.topo-ov-eye');
                if (eye) { this._applyEye(eye, !!on); }
                const centerBtn = rowEl.querySelector('.topo-ov-center');
                if (centerBtn) { centerBtn.classList.toggle('is-disabled', !on); }
            }
            if (item && String(id) === String(this._selectedId)) { this._renderFlyout(item); }
        }

        destroy() {
            this.close();
            if (this._panel && this._panel.parentNode) { this._panel.parentNode.removeChild(this._panel); }
            if (this._flyout && this._flyout.parentNode) { this._flyout.parentNode.removeChild(this._flyout); }
            // Button aus den Map-Controls (MVCArray) entfernen — sonst bleibt bei destroy+rebuild (View-Wechsel) je ein
            // toter Button stehen (X Buttons nach X Wechseln).
            if (this._ctrlPos !== undefined && this._map && this._map.controls) {
                const arr = this._map.controls[this._ctrlPos];
                if (arr) {
                    for (let i = arr.getLength() - 1; i >= 0; i--) {
                        if (arr.getAt(i) === this._button) { arr.removeAt(i); }
                    }
                }
            }
        }
    };
}
